import { ListingPosition } from './../../../listing/shared/listing-position.model';
import { MembershipLevelService } from './../../../membership-level/shared/membership-level.service';
import { Component, OnChanges, ViewChildren, QueryList, Input, OnInit, EventEmitter, Output, SimpleChanges } from '@angular/core';
import {
  ObjectValueAccessor,
  CreateObjectValueAccessorProvider,
  CreateObjectValueValidationProvider,
} from '../../../shared/widgets/value-accessors/object-value-accessor';
import { ProfileService } from '../../shared/profile.service';
import { Listing } from '../../../listing/shared/listing.model';
import { ListingService } from '../../../listing/shared/listing.service';
import { ProfileEditListingComponent } from './profile-edit-listing.component';
import { ProfileRead } from '../../shared/profile-read.model';
import { getSafe } from 'src/app/shared/util/get-safe';
import { AuthService, TranslationService, UtilService } from 'src/app/shared';
import { ProfileRelationSearch } from '../../shared/profile-relation-search.model';
import { SelectItem } from 'primeng/api';
import { ListingType } from '../../../listing/shared/listing-type.model';
import { ListingCategory } from '../../../listing-catagories/listing-category.model';
import { Entity } from '../../../entity/shared/entity.model';
import { EntityDomainDto } from '../../../affcode/shared/dto/entity-domain.dto';
import { NavigationService } from '../../../shared/navigation/navigation.service';
import { EntityService } from '../../../entity/shared/entity.service';
import { EntityDomainService } from '../../../admin/entity-domains/shared/entity-domain.service';
import { ListingCategoryService } from '../../../listing-catagories/listing-category.service';
import { ObservationService } from '../../../shared/observation/observation.service';
import { ConfigServiceService } from '../../../config/shared/config-service.service';
import { WebComponentSettingsDto } from '../../../communication/public-links/shared/dto/web-component-settings.dto';

@Component({
  selector: 'wl-profile-edit-listing-list',
  templateUrl: 'profile-edit-listing-list.component.html',
  providers: [
    CreateObjectValueAccessorProvider(ProfileEditListingListComponent),
    CreateObjectValueValidationProvider(ProfileEditListingListComponent),
  ],
    standalone: false
})
export class ProfileEditListingListComponent extends ObjectValueAccessor<Array<Listing>> implements OnInit, OnChanges {
  @Input() profileId: number;
  @Input() profile: ProfileRead;
  @Output() saveAndRedirectClicked = new EventEmitter<void>();
  private _tabIsSelected = false;
  @Input() public get tabIsSelected() {
    return this._tabIsSelected;
  }
  set tabIsSelected(value: boolean) {
    this._tabIsSelected = value;
  }
  @ViewChildren(ProfileEditListingComponent) _listingComponents: QueryList<ProfileEditListingComponent>;

  listingTypes: Array<ListingType>;
  categories: Array<ListingCategory>;
  listingPositions: Array<ListingPosition>;
  entities: Array<Entity>;
  entityDomains: Array<EntityDomainDto>;
  tenantHttpsUrl: string;
  listingToView: Listing;
  openViewListingDialog = false;
  isSaving: boolean = false;
  selectedListingBaseUrl: string;
  selectedListingDirectoryUrl: string;
  showViewListingButton: boolean;
  redirectSettings: Map<string, boolean> = new Map<string, boolean>();

  relatedProfileSelectItems = new Array<SelectItem>();
  selectedIndex = 0;

  public constructor(
    private _profileService: ProfileService,
    private _listingService: ListingService,
    private _membershipLevelService: MembershipLevelService,
    private _translationService: TranslationService,
    private _utilService: UtilService,
    private _authService: AuthService,
    private _entityService: EntityService,
    private _entityDomainService: EntityDomainService,
    private _listingCategoryService: ListingCategoryService,
    private _observationService: ObservationService,
    private _configApiService: ConfigServiceService
  ) {
    super([], false);
  }

  ngOnInit() {
    this._observationService.observeProfiles().subscribe((id) => {
      if (id === this.profileId) {
        this.isSaving = false;
      }
    });

    this._listingService.getListingPositions().then((positions) => {
      this.listingPositions = positions;
    });

    this._listingService.getListingTypes().then((data) => {
      this.listingTypes = data;
    });

    this._listingCategoryService.search({ PageNumber: 0, PageSize: 0, OrderBy: 'Category' }).then((data) => {
      this.categories = data.Result;
    });

    this._entityService.getEntities().then((data) => {
      this.entities = data;
    });

    this._entityDomainService.getAllEntityDomains().then((data) => {
      this.entityDomains = data;

      // Save redirect settings in a hash map for each domain so we can look up later
      // if the domain is configured to redirect to the new listing directory.
      const settingPromises: Promise<WebComponentSettingsDto>[] = this.entityDomains.map((entityDomain) => {
        return this._configApiService.getWebComponentSettings(entityDomain.SettingBatch);
      });
      Promise.all(settingPromises)
        .then((settings) => {
          settings.forEach((setting, index) => {
            if (!this.redirectSettings.has(this.entityDomains[index].Domain)) {
              this.redirectSettings.set(this.entityDomains[index].Domain, setting ? setting.RedirectLegacyToNew : false);
            }
          });
        })
        .finally(() => {
          this.setShowViewListingButton();
        });
    });

    this.tenantHttpsUrl = this._authService.getTenantHttpsUrl();

    this.relatedProfileSelectItems.push({
      label: this._translationService.getInstantTitlecase('listing.no-profile'),
      value: 0,
    } as SelectItem);

    let search: ProfileRelationSearch = new ProfileRelationSearch();
    search.ProfileId = this.profileId;

    this._profileService.getProfileViewPrimaryRelations(search).then((relatedProfilesData) => {
      relatedProfilesData.Result = this._utilService.sortByStringAsc(relatedProfilesData.Result, 'ReportName');

      this.relatedProfileSelectItems.push(
        ...relatedProfilesData.Result.map<SelectItem>((x) => {
          return { label: `${x.ReportName} (${x.ProfileId})`, value: x.ProfileId };
        })
      );
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.profileId) {
      if (this.profileId) {
        this._profileService.getAllProfileListings(this.profileId).then((data) => {
          this.value = data;
          if (this.value.length > 0) {
            this.listingToView = this.value[0];
            this.setShowViewListingButton();
          }
          this.setPristine();
        });
      }
    }
  }

  addListing() {
    const listing = {} as Listing;
    // if there's only one listing, it's primary
    this.setDefaults(listing);
    if (this.value.length === 0) {
      listing.PrimaryListing = true;
    }
    listing.WebListing = true;
    listing.PrintListing = true;
    listing.Active = true;
    listing.ShowMap = true;
    listing.CategorySubCategory = this._translationService.getInstantTitlecase('common.new');
    //Instantiate Descr property with an empty value so we can do a null-check elsewhere
    listing.Descr = '';
    this.value.push(listing);
    setTimeout(() => {
      this.selectedIndex = this.value.length - 1;
      this.listingToView = this.value[this.selectedIndex];
      this.setShowViewListingButton();
    }, 50);
  }

  deleteListing(index: number) {
    this.value.splice(index, 1);
    setTimeout(() => {
      this.selectedIndex = index - 1;
      if (index >= 0) {
        this.listingToView = this.value[this.selectedIndex];
        this.setShowViewListingButton();
      }
    }, 50);
  }

  save() {
    const unchangedListings: Array<Listing> = JSON.parse(this.pristineValue);
    const deletedListings: Array<Listing> = unchangedListings.filter((x) => !this.value.some((a) => x.ListingID == a.ListingID));
    const listingPromises: Array<Promise<void>> = [];

    this._listingComponents.forEach((component) => {
      listingPromises.push(component.save(this.profileId));
    });

    for (let listing of deletedListings) {
      listingPromises.push(this._listingService.deleteListing(listing.ListingID));
    }

    return Promise.all(listingPromises).then(() => {
      this.setPristine();
    });
  }

  /*
   * When one of our children gets selected as primary
   */
  onPrimaryListingSelected(listing: Listing) {
    // there can be only one primary listing
    this.value.filter((x) => x != listing && x.PrimaryListing).forEach((x) => (x.PrimaryListing = false));
  }

  copyListing($event: Listing) {
    for (let listing of this.value.filter((x) => $event != x)) {
      listing.Descr = $event.Descr;
      listing.ShortDescr = $event.ShortDescr;
      listing.ProductsAndServices = $event.ProductsAndServices;
      listing.Keywords = $event.Keywords;
      this._listingComponents.find((x) => x.value == listing).htmlDescr = listing.Descr;
    }
  }

  public onNavigateToListingDirectoryUrl(index: number) {
    this.listingToView = this.value[index];
    if (this.isDirty()) {
      this.openViewListingDialog = true;
      return;
    }
    this.handleNavigateToListingDirectoryUrl();
  }

  public onTabOrListingChanged(idx: number) {
    if (this._utilService.isNotNullOrUndefined(idx)) {
      this.listingToView = this.value[idx];
      this.setShowViewListingButton();
    }
  }

  public handleSaveClick() {
    this.isSaving = true;
    this.saveAndRedirectClicked.emit();
  }

  public handleCancelClick() {
    this.openViewListingDialog = false;
  }

  public handleNavigateToListingDirectoryUrl() {
    if (!this.listingToView.ListingID) {
      this.openViewListingDialog = false;
      return;
    }
    this.openViewListingDialog = false;
    this.setListingDirectoryUrl();
    window.open(this.selectedListingDirectoryUrl, '_blank');
  }

  public handleListingEntityChanged(listing: Listing) {
    this.listingToView = listing;
    this.setShowViewListingButton();
  }

  public handleCategoryChanged(listing: Listing) {
    this.listingToView = listing;
    this.setShowViewListingButton();
  }

  private setDefaults(listing: Listing) {
    listing.ListingTypeID = 0;
    listing.DetailsListingTypeID = 0;
    listing.ListingPrefix = 'ZZ';
    if (this.profile && this.profile.MembershipLevelId) {
      this._membershipLevelService.getMembershipLevel(this.profile.MembershipLevelId).then((membershipLevel) => {
        if (membershipLevel.OverrideListingOptions) {
          listing.ListingTypeID = membershipLevel.ListingResultsTypeOverride;
          listing.DetailsListingTypeID = membershipLevel.ListingDetailTypeOverride;
          listing.ListingPrefix = getSafe(
            () => this.listingPositions.find((x) => x.ListingPositionId === membershipLevel.ListingPositionOverride).ListingPrefix,
            'ZZ'
          );
        }
      });
    }
  }

  private setListingDirectoryUrl() {
    if (!this.listingToView || !this.categories) return;

    const category = this.categories.find((x) => x.CategoryID == this.listingToView.CategoryID);
    let categoryUrlName = (category?.Category) ?? '';
    categoryUrlName = categoryUrlName.replace(/\s/g, '-').replace(/\+/g, '-').replace(/\//g, '-').toLowerCase();
    categoryUrlName = encodeURIComponent(categoryUrlName);

    const overrideUrl =
      this.listingToView.OverrideProfileSettings && this._utilService.isNotNullEmptyOrWhitespace(this.listingToView.DisplayName);
    
    let listingUrlName = overrideUrl ? this.listingToView.DisplayName : (this.profile?.ReportName ?? this.profile?.DBA ?? '');
    listingUrlName = listingUrlName.replace(/\s/g, '-').replace(/\+/g, '-').replace(/\//g, '-').replace(/\./g, '-').toLowerCase();
    listingUrlName = encodeURIComponent(`${listingUrlName}-${this.listingToView.ListingID}`);
    
    this.selectedListingDirectoryUrl =
      this.selectedListingBaseUrl + '/atlas/directory/category' + '/' + categoryUrlName + '/' + listingUrlName;
  }

  private setShowViewListingButton() {
    if (!this.listingToView) {
      this.showViewListingButton = false;
      return;
    }

    if (this._utilService.isNullOrUndefined(this.listingToView.CategoryID)) {
      this.showViewListingButton = false;
      return;
    }

    let entity = undefined;
    let baseUrl = this.tenantHttpsUrl;
    
    let domain = '';
    try {
      const baseUrlObject = new URL(baseUrl);
      domain = baseUrlObject.hostname;
    } catch (e) {
      console.warn('Invalid tenantHttpsUrl:', baseUrl);
    }

    let entityDomain = this.entityDomains.find((x) => x.Domain == domain);

    entity = this.entities.find((x) => x.EntityID == this.listingToView.EntityID);
    if (entity) {
      const tempEntDomain = this.entityDomains.find((x) => x.EntityID == entity.EntityID);
      entityDomain = tempEntDomain ? tempEntDomain : entityDomain;
    }

    if (!entityDomain) {
      this.showViewListingButton = false;
      return;
    }

    baseUrl = entityDomain.Domain.includes('http') ? entityDomain.Domain : 'https://' + entityDomain.Domain;
    this.selectedListingBaseUrl = baseUrl;
    this.showViewListingButton = this.redirectSettings.get(entityDomain.Domain) || false;
  }
}
